import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { Building2, Plus, Search, RefreshCw } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useOrganizations, useCreateOrganization } from "@/hooks/use-organizations";
import { useIsPlatformAdmin } from "@/lib/auth";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, SortableHeader } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@fox1ck-org/ui";

const PAGE_SIZE = 20;

type OrgRow = {
  id: string;
  name: string;
  alias?: string;
  description?: string;
  enabled: boolean;
  metadata?: { id?: string; slug?: string; plan?: string } | null;
  membership?: { roles: string[] };
};

const columns: ColumnDef<OrgRow>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column}>Название</SortableHeader>,
    cell: ({ row }) => (
      <Link
        to={`/organizations/${row.original.metadata?.id ?? row.original.id}`}
        className="font-medium text-foreground hover:text-primary flex items-center gap-2"
      >
        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
        <div>
          <span>{row.original.name}</span>
          {row.original.description && (
            <p className="text-xs text-muted-foreground mt-0.5">{row.original.description}</p>
          )}
        </div>
      </Link>
    ),
  },
  {
    accessorKey: "slug",
    header: "Код",
    cell: ({ row }) => (
      <span className="text-muted-foreground font-mono text-sm">
        {row.original.metadata?.slug ?? row.original.alias}
      </span>
    ),
  },
  {
    accessorKey: "plan",
    header: "Тариф",
    cell: ({ row }) => (
      <Badge variant="neutral">{row.original.metadata?.plan ?? "free"}</Badge>
    ),
  },
  {
    accessorKey: "enabled",
    header: "Статус",
    cell: ({ row }) =>
      row.original.enabled ? (
        <Badge variant="ok">Активна</Badge>
      ) : (
        <Badge variant="danger">Отключён</Badge>
      ),
  },
  {
    accessorKey: "membership",
    header: "Роль",
    cell: ({ row }) =>
      row.original.membership ? (
        <span className="text-xs text-muted-foreground">{row.original.membership.roles.join(", ")}</span>
      ) : (
        <span className="text-xs text-muted-foreground">-</span>
      ),
  },
];

export default function Organizations() {
  const isPlatformAdmin = useIsPlatformAdmin();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setPage(0), [debouncedSearch]);

  const { data, isLoading, refetch } = useOrganizations({
    search: debouncedSearch || undefined,
    first: page * PAGE_SIZE,
    max: PAGE_SIZE,
  });

  const orgs = data?.organizations ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title="Организации" description="Организации и их участники">
        <Button variant="ghost" size="icon" onClick={() => refetch()}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
        </Button>
        {isPlatformAdmin && (
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" /> Новая организация
          </Button>
        )}
      </PageHeader>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск организаций…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground">{orgs.length} organization{orgs.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={orgs}
        isLoading={isLoading}
        showPagination={false}
        emptyState={
          <EmptyState
            icon={<Building2 className="h-10 w-10" />}
            title={debouncedSearch ? "Ничего не нашлось" : "Организаций пока нет"}
            action={isPlatformAdmin && !debouncedSearch ? { label: "Новая организация", onClick: () => setShowCreate(true) } : undefined}
          />
        }
      />

      {/* Server-side pagination */}
      {orgs.length >= PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
            Назад
          </Button>
          <span className="text-sm text-muted-foreground">Page {page + 1}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={orgs.length < PAGE_SIZE}>
            Вперёд
          </Button>
        </div>
      )}

      {showCreate && <CreateOrgDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function CreateOrgDialog({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [plan, setPlan] = useState("free");
  const [maxMembers, setMaxMembers] = useState("");
  const [storageQuotaGB, setStorageQuotaGB] = useState("");
  const [domainInput, setDomainInput] = useState("");
  const [domains, setDomains] = useState<string[]>([]);
  const createOrg = useCreateOrganization();

  const autoSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  function addDomain() {
    const d = domainInput.trim().toLowerCase();
    if (d && !domains.includes(d)) {
      setDomains([...domains, d]);
      setDomainInput("");
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await createOrg.mutateAsync(
      {
        name,
        slug: slug || autoSlug,
        description: description || undefined,
        plan,
        maxMembers: maxMembers ? parseInt(maxMembers) : undefined,
        storageQuotaBytes: storageQuotaGB ? parseInt(storageQuotaGB) * 1_073_741_824 : undefined,
        domains: domains.length > 0 ? domains : undefined,
      },
      {
        onSuccess: () => {
          toast.success("Организация создана");
          onClose();
        },
        onError: (err) => toast.error(`Ошибка: ${err.message}`),
      }
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Новая организация</DialogTitle>
          <DialogDescription>Добавить организацию на платформу.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Название</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="например, Команда дизайна" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Код</label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={autoSlug || "auto-generated"} className="font-mono" />
            </div>
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Описание</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Описание, если нужно" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Тариф</label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Лимит участников</label>
              <Input type="number" value={maxMembers} onChange={(e) => setMaxMembers(e.target.value)} placeholder="Без лимита" min={1} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Хранилище (ГБ)</label>
              <Input type="number" value={storageQuotaGB} onChange={(e) => setStorageQuotaGB(e.target.value)} placeholder="Без лимита" min={1} />
            </div>
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Домены</label>
            <div className="flex gap-2">
              <Input
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder="example.com"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDomain(); } }}
              />
              <Button type="button" variant="outline" size="sm" onClick={addDomain} disabled={!domainInput.trim()}>Добавить</Button>
            </div>
            {domains.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {domains.map((d) => (
                  <Badge key={d} variant="neutral" className="gap-1">
                    {d}
                    <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => setDomains(domains.filter((x) => x !== d))}>
                      &times;
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          {createOrg.isError && (
            <p className="text-sm text-destructive">{createOrg.error.message}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Отмена</Button>
            <Button type="submit" disabled={!name || createOrg.isPending}>
              {createOrg.isPending ? "Создаём…" : "Создать"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
